import VbivStatus from '@/components/vbiv-status';
import { useOpenClient } from '@/hooks/use-open-client';
import type { ICardListItem } from '@/entities/card.entity';
import CardProviderLabel from '@/components/card-provider-label';
import { BanknoteIcon, CalendarClockIcon, CircleDollarSignIcon, ContainerIcon, HashIcon } from 'lucide-react';
import { memo, useCallback, type MouseEvent } from 'react';
import { toast } from 'sonner';
import TimeAgo from 'react-timeago';
import russianStrings from 'react-timeago/lib/language-strings/ru';
import buildFormatter from 'react-timeago/lib/formatters/buildFormatter';

const formatter = buildFormatter(russianStrings);
const money = new Intl.NumberFormat('en-US', { style: 'decimal', maximumFractionDigits: 0 });

const CardListItem = memo(function CardListItem({ card }: { card: ICardListItem }) {
	const { openClient } = useOpenClient();
	const goToClient = useCallback(() => {
		openClient(card.clientId, { listType: 'cards', clientId: card.clientId, itemId: card.id });
	}, [card.clientId, card.id, openClient]);

	function handleCopy(e: MouseEvent, text: string) {
		e.stopPropagation();
		navigator.clipboard
			.writeText(text)
			.then(() => toast.success('Скопировано'))
			.catch(() => toast.error('Ошибка копирования'));
	}

	return (
		<div
			role='button'
			tabIndex={0}
			className='relative p-2 bg-card rounded-md flex flex-col gap-1.5 border-border border border-l-2 overflow-hidden cursor-pointer active:bg-accent/40'
			onClick={goToClient}
			onKeyDown={e => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					goToClient();
				}
			}}
		>
			<div className='flex justify-between items-center'>
				{card.number ? (
					<CardProviderLabel
						card={card}
						className='text-sm hover:bg-accent cursor-pointer select-none text-chart-3 font-semibold'
						onClick={e => handleCopy(e, card.number || '')}
					/>
				) : null}
				<div onClick={e => e.stopPropagation()}>
					<VbivStatus clientId={card.clientId} takeStatus={card.takeStatus} takenFrom={card.takenFrom} />
				</div>
			</div>

			<div className='flex justify-between'>
				<span className='flex gap-1 items-center break-all'>
					<div className='w-[18px]'>
						<HashIcon size={16} className='opacity-40' />
					</div>
					<span className='text-sm hover:bg-accent cursor-pointer select-none' onClick={e => handleCopy(e, '#' + card.clientId)}>
						{card.clientId}
					</span>
				</span>
			</div>

			<div className='flex justify-between'>
				<span className='flex gap-1 items-center'>
					<div className='w-[18px]'>
						<ContainerIcon size={16} className='opacity-40' />
					</div>
					<span className='text-sm hover:bg-accent cursor-pointer select-none' onClick={e => handleCopy(e, card.buildId)}>
						{card.buildId}
					</span>
				</span>
			</div>

			{card.date && (
				<div className='flex justify-between'>
					<span className='flex gap-1 items-center'>
						<div className='w-[18px]'>
							<BanknoteIcon size={16} className='opacity-40' />
						</div>
						<span className='text-sm'>{card.date}</span>
					</span>
				</div>
			)}

			{card.balance != null && (
				<div className='flex justify-between'>
					<span className='flex gap-1 items-center'>
						<div className='w-[18px]'>
							<CircleDollarSignIcon size={16} className='opacity-40' />
						</div>
						<span
							className='text-sm hover:bg-accent cursor-pointer select-none text-destructive font-semibold'
							onClick={e => handleCopy(e, String(card.balance))}
						>
							{money.format(card.balance)}
						</span>
					</span>
				</div>
			)}

			<span className='flex gap-1 items-center'>
				<div className='w-[18px]'>
					<CalendarClockIcon size={16} className='opacity-40' />
				</div>
				<span className='text-sm font-semibold opacity-40'>Создан</span>
				<div className='text-sm text-chart-2 opacity-95'>
					<TimeAgo date={card.createdAt} formatter={formatter} />
				</div>
			</span>
		</div>
	);
});

export default CardListItem;
